// src/pages/WebhookLogs.js
import React, { useEffect, useState } from 'react';
import { getAllWebhookLogs, getWebhookLogs } from '../services/api';

const STATUS_FILTERS = ['', 'PROCESSED', 'FAILED', 'DUPLICATE', 'ORDER_NOT_FOUND', 'RECEIVED', 'IGNORED'];

function statusBadge(status) {
  const map = {
    PROCESSED:       'badge-success',
    DUPLICATE:       'badge-warning',
    IGNORED:         'badge-default',
    FAILED:          'badge-danger',
    ORDER_NOT_FOUND: 'badge-danger',
    RECEIVED:        'badge-info',
  };
  return map[status] || 'badge-default';
}

function fmt(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
}

export default function WebhookLogs() {
  const [logs,       setLogs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchOrder, setSearchOrder]   = useState('');
  const [expanded,   setExpanded]   = useState(null);
  const [error,      setError]      = useState('');

  const loadLogs = async (status = '') => {
    setLoading(true);
    setError('');
    try {
      const res = await getAllWebhookLogs(status);
      setLogs(res.data?.data || []);
    } catch (err) {
      setError('Failed to load webhook logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLogs(); }, []);

  const handleStatusFilter = (s) => {
    setStatusFilter(s);
    loadLogs(s);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchOrder.trim()) { loadLogs(statusFilter); return; }
    setLoading(true);
    setError('');
    try {
      const res = await getWebhookLogs(searchOrder.trim());
      setLogs(res.data?.data || []);
    } catch {
      setError('Failed to search webhook logs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Webhook Logs</h1>
          <p>Razorpay payment webhook events</p>
        </div>
        <button className="btn btn-secondary" onClick={() => loadLogs(statusFilter)}>
          🔄 Refresh
        </button>
      </div>

      <div className="card">
        {/* Filters */}
        <div className="filter-bar" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <select value={statusFilter} onChange={e => handleStatusFilter(e.target.value)}>
            {STATUS_FILTERS.map(s => (
              <option key={s} value={s}>{s || 'All Statuses'}</option>
            ))}
          </select>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              placeholder="Search by Razorpay Order ID..."
              value={searchOrder}
              onChange={e => setSearchOrder(e.target.value)}
              style={{ minWidth: 300 }}
            />
            <button type="submit" className="btn btn-secondary btn-sm">Search</button>
            {searchOrder && (
              <button type="button" className="btn btn-secondary btn-sm"
                onClick={() => { setSearchOrder(''); loadLogs(statusFilter); }}>
                Clear
              </button>
            )}
          </form>
          <span style={{ color: '#666', fontSize: 13 }}>{logs.length} events</span>
        </div>

        {error && <div className="alert-banner error">{error}</div>}

        {loading ? (
          <div className="loading"><div className="spinner"/>Loading...</div>
        ) : logs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <p>No webhook events found</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Received At</th>
                  <th>Event Type</th>
                  <th>Razorpay Order ID</th>
                  <th>Payment ID</th>
                  <th>Status</th>
                  <th>Error</th>
                  <th>Payload</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => (
                  <React.Fragment key={log.id}>
                    <tr>
                      <td style={{ color: '#888' }}>{idx + 1}</td>
                      <td style={{ fontSize: 12 }}>{fmt(log.receivedAt)}</td>
                      <td><code style={{ fontSize: 11 }}>{log.eventType}</code></td>
                      <td><code style={{ fontSize: 11 }}>{log.razorpayOrderId || '—'}</code></td>
                      <td><code style={{ fontSize: 11 }}>{log.razorpayPaymentId || '—'}</code></td>
                      <td><span className={`badge ${statusBadge(log.status)}`}>{log.status}</span></td>
                      <td style={{ fontSize: 11, color: '#dc3545', maxWidth: 150 }}>
                        {log.errorMessage || '—'}
                      </td>
                      <td>
                        {log.payload && (
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ fontSize: 11 }}
                            onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                          >
                            {expanded === log.id ? 'Hide' : 'View'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded === log.id && log.payload && (
                      <tr>
                        <td colSpan={8}>
                          <pre style={{
                            background: '#f8f9fa', padding: 12, borderRadius: 6,
                            fontSize: 11, overflow: 'auto', maxHeight: 300,
                            border: '1px solid #dee2e6', margin: '4px 0'
                          }}>
                            {(() => { try { return JSON.stringify(JSON.parse(log.payload), null, 2); } catch { return log.payload; } })()}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
